import type { GetConsultationTypeReturn } from '@/components/PatientPageV2/utils/getConsultationType';
import type { ConsultPaymentState } from '@/hooks/consult/useConsult';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import { useConsultationStore } from '@/state-management/slices/consultations/hooks/useConsultationStore';
import type { AuthData, ConsultationPricingTier } from '@/types';
import { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { Alert, AlertTitle, Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { ShowConsultation } from './ShowConsultation';

interface ConsultationRatePaymentProps {
  authData: AuthData;
  consultAndClinicianType: GetConsultationTypeReturn;
  paymentStatus: ConsultPaymentState;
  handlePaymentRequest: (paymentRequestType: PaymentRequestType) => Promise<void>;
  isPending: boolean;
  pricingTier: ConsultationPricingTier | undefined;
  consultation: ShowConsultation;
}

const TOGGLE_BUTTON_GROUP_OPTIONS = [
  {
    label: 'STANDARD',
    description: 'FULL PRICE',
    value: PaymentRequestType.STANDARD,
  },
  {
    label: 'CONCESSION',
    description: '15% OFF',
    value: PaymentRequestType.CONCESSION,
  },
  {
    label: 'COMPLIMENTARY',
    description: 'FREE',
    value: PaymentRequestType.COMPLIMENTARY,
  },
] as const;

const ConsultationRatePaymentOptions = ({
  paymentStatus,
  handlePaymentRequest,
  isPending,
  pricingTier,
  consultation,
}: ConsultationRatePaymentProps) => {
  const patientHasBeenSentPaymentLink = pricingTier;
  const paymentButtonText = patientHasBeenSentPaymentLink ? 'Resend Payment Link' : 'Charge Patient';
  const { paymentRequestType, setConsultationField } = useConsultationStore(consultation);

  const currentPaymentRequestType = paymentRequestType ?? PaymentRequestType.STANDARD;

  return (
    <Box component="section">
      <SectionHeader headerText="Payment Details" />
      <Box mt={8} mb={4} maxWidth={500}>
        {patientHasBeenSentPaymentLink && paymentStatus !== 'PAID' && (
          <Alert severity="info" sx={{ marginBottom: 4 }}>
            <AlertTitle>Payment Link Sent</AlertTitle>
            The patient has already been sent a payment link to pay for a {pricingTier.name} consultation.
          </Alert>
        )}
        {paymentStatus === 'PAID' ? (
          <Alert severity="success" sx={{ marginBottom: 4 }}>
            <AlertTitle>Payment Confirmed</AlertTitle>
            The consultation fee has been successfully processed and paid.
          </Alert>
        ) : (
          <>
            <ToggleButtonGroup
              color="primary"
              value={currentPaymentRequestType}
              exclusive
              onChange={(_event: React.MouseEvent<HTMLElement>, newValue: PaymentRequestType | null) => {
                if (newValue !== null) {
                  setConsultationField(consultation.id, 'paymentRequestType', newValue);
                }
              }}
              aria-label="Payment Details"
              fullWidth
              sx={{
                display: 'flex',
              }}
            >
              {!patientHasBeenSentPaymentLink &&
                TOGGLE_BUTTON_GROUP_OPTIONS.map((option) => (
                  <ToggleButton
                    key={option.label}
                    value={option.value}
                    sx={{ flex: 1, flexDirection: 'column', fontStyle: 'uppercase', marginBottom: 4 }}
                    disabled={isPending}
                  >
                    <Typography fontWeight={500} color="primary">
                      {option.label}
                    </Typography>
                    <Typography fontSize="small" color="text.secondary">
                      {option.description}
                    </Typography>
                  </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Button
              size="large"
              color="primary"
              variant="contained"
              sx={{
                marginBottom: 4,
              }}
              fullWidth
              onClick={() => handlePaymentRequest(currentPaymentRequestType)}
              disabled={isPending}
            >
              {paymentButtonText}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ConsultationRatePaymentOptions;
